/** biome-ignore-all lint/style/useComponentExportOnlyModules: storybook export pattern */
'use client'

import { Button as BaseButton } from '@base-ui/react/button'
import { cva, type VariantProps } from 'class-variance-authority'
import flattenChildren from 'react-keyed-flatten-children'
import { isValidElement, cloneElement } from 'react'
import type * as React from 'react'
import { cn } from '../lib/utils'
import { mergeProps } from '@base-ui/react/merge-props'
import { isIcon, isLabel, processIcon } from '../lib/children-utils'
import { LoadingContent } from '../lib/loading-content'
import { useClickAnimation } from '../hooks/use-click-animation'
import {
  type RelativeColorName,
  relativeColorClasses,
} from '../lib/relative-color'
import { Avatar, AvatarFallback } from './avatar'
import { XIcon } from './icon'

// INFO: I think assist chips must have an icon to stand out from suggestion chips.
// oxlint-disable-next-line no-warning-comments -- TODO preserved per repo policy
// TODO: Chips can be dragged.

export const chipVariants = cva(
  'focus-visible:ring-primary/80 focus-visible:ring-offset-surface-1 relative inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-[8px] border text-sm font-medium whitespace-nowrap transition-colors outline-none select-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 [@media(hover:none)]:disabled:pointer-events-none',
  {
    variants: {
      variant: {
        assist: 'border-border bg-surface-2 text-on-surface px-3 py-2',
        input: 'border-border bg-surface-2 text-on-surface',
        filter:
          'border-border bg-surface-2 text-on-surface data-[selected=true]:bg-border min-h-9 px-3 py-2 data-[selected=true]:border-transparent',
        suggestion:
          'border-border bg-surface-2 text-on-surface min-h-9 px-3 py-2',
        tag: 'text-label-big min-h-8 cursor-default rounded-full border-transparent px-3 py-1 font-normal',
      },
    },
    defaultVariants: {
      variant: 'assist',
    },
  },
)

interface Chip extends BaseButton.Props {
  variant?: VariantProps<typeof chipVariants>['variant']
  tagColor?: ChipTagColor
  selected?: boolean
  onRemove?: () => void
  loading?: boolean
  trailing?: React.ReactNode
  onTrailingClick?: () => void
}

export type ChipTagColor = RelativeColorName

function Chip({
  className,
  variant = 'assist',
  tagColor = 'gray',
  selected,
  onRemove,
  disabled,
  loading,
  trailing,
  onTrailingClick,
  children,
  render,
  nativeButton,
  ...props
}: Chip) {
  const clickAnimation = useClickAnimation()

  const { icon, avatar, label } = extractChildren(children, variant)
  const processedIcon = icon ? processIcon(icon, 20) : null

  let content: React.ReactNode

  if (variant === 'filter') {
    content = (
      <>
        {selected && (
          <svg
            className="size-[18px] shrink-0"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
        {!selected && processedIcon}
        <span className="inline-flex place-items-center gap-3">
          <span className="element">{label}</span>
          {trailing && (
            // oxlint-disable-next-line jsx-a11y/no-static-element-interactions -- role="button" is dynamically set when onTrailingClick is provided; key handler also present
            <span
              role={onTrailingClick ? 'button' : undefined}
              tabIndex={onTrailingClick && !disabled ? 0 : undefined}
              onClick={
                onTrailingClick
                  ? (e) => {
                      e.stopPropagation()
                      if (!disabled) onTrailingClick()
                    }
                  : undefined
              }
              onKeyDown={
                onTrailingClick
                  ? (e) => {
                      if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
                        e.stopPropagation()
                        e.preventDefault()
                        onTrailingClick()
                      }
                    }
                  : undefined
              }
              className={cn(
                'p-2 [&>svg]:size-5',
                onTrailingClick &&
                  'focus-visible:ring-primary/80 cursor-pointer rounded-full focus:outline-none focus-visible:ring-2',
                onTrailingClick && disabled && 'pointer-events-none',
              )}
            >
              {trailing}
            </span>
          )}
        </span>
      </>
    )
  } else if (variant === 'input') {
    content = (
      <>
        {/* oxlint-disable-next-line typescript/prefer-nullish-coalescing -- intentional: ReactNode || fallback JSX, avatar can be false/null */}
        {avatar || (processedIcon && <span>{processedIcon}</span>)}

        <span className="inline-flex place-items-center gap-1">
          <span className="element">{label}</span>

          {onRemove && (
            // The chip is itself a <button>; HTML forbids a nested <button>, so the remove action is a
            // role="button" span (same pattern as the filter chip's trailing action).
            <span
              role="button"
              tabIndex={disabled ? -1 : 0}
              onClick={(e) => {
                e.stopPropagation()
                if (!disabled) onRemove()
              }}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
                  e.stopPropagation()
                  e.preventDefault()
                  onRemove()
                }
              }}
              aria-label="Remove"
              aria-disabled={disabled}
              className={cn('inline-block cursor-pointer p-2', disabled && 'cursor-not-allowed')}
            >
              <XIcon size={20} />
            </span>
          )}
        </span>
      </>
    )
  } else if (variant === 'suggestion') {
    content = <span className="element">{label}</span>
  } else {
    // assist
    content = (
      <LoadingContent loading={loading} size={20}>
        {processedIcon}
        <span className="element">{label}</span>
      </LoadingContent>
    )
  }

  return (
    <BaseButton
      className={cn(
        chipVariants({ variant }),
        variant === 'assist' && icon && 'pl-3',
        variant === 'input' && !avatar && 'pl-3',
        variant === 'filter' && trailing && 'py-0 pr-0',
        variant === 'tag' && relativeColorClasses[tagColor],
        loading && '!cursor-wait',
        clickAnimation.className,
        className,
      )}
      data-slot="chip"
      data-testid={label}
      data-selected={variant === 'filter' ? selected : undefined}
      // oxlint-disable-next-line typescript/prefer-nullish-coalescing -- intentional: boolean || boolean, disabled can be false
      disabled={disabled || loading}
      render={render}
      nativeButton={
        nativeButton ?? !(isValidElement(render) && render.type === 'a')
      }
      {...mergeProps(clickAnimation.handlers, props)}
    >
      {content}
    </BaseButton>
  )
}

function isAvatar(child: React.ReactNode): boolean {
  return isValidElement(child) && child.type === Avatar
}

function extractChildren(children: React.ReactNode, variant?: Chip['variant']) {
  const childArray = flattenChildren(children)

  let icon: React.ReactNode | null = null
  let avatar: React.ReactNode | null = null
  let label: React.ReactNode | null = null

  for (const child of childArray) {
    if (isIcon(child)) {
      if (variant === 'suggestion') {
        throw new Error('Suggestion chips cannot have icons.')
      }
      if (icon !== null) {
        throw new Error('Chip accepts only one icon.')
      }
      icon = child
    } else if (isAvatar(child)) {
      if (variant !== 'input') {
        throw new Error('Only input chips can have avatars.')
      }
      if (avatar !== null) {
        throw new Error('Chip accepts only one avatar.')
      }
      const avatarChild = child as React.ReactElement<{
        className?: string
        children?: React.ReactNode
      }>
      // Process Avatar's children to add rounded-lg to fallback
      const processedChildren = flattenChildren(avatarChild.props.children).map(
        (avatarSubChild) => {
          if (
            isValidElement(avatarSubChild) &&
            avatarSubChild.type === AvatarFallback
          ) {
            return cloneElement(
              avatarSubChild as React.ReactElement<{ className?: string }>,
              {
                className: cn(
                  'rounded-[8px]',
                  (
                    avatarSubChild as React.ReactElement<{
                      className?: string
                    }>
                  ).props.className,
                ),
              },
            )
          }
          return avatarSubChild
        },
      )
      avatar = cloneElement(avatarChild, {
        className: cn('size-9 rounded-[8px]', avatarChild.props.className),
        children: processedChildren,
      })
    } else if (isLabel(child)) {
      if (label !== null) {
        throw new Error('Chip expects only one label.')
      }
      label = child
    } else {
      const childType = isValidElement(child) ? 'React element' : typeof child
      throw new Error(
        `Chip: invalid child type (${childType}). Children must be an icon, avatar (input only), or label.`,
      )
    }
  }

  if (label === null) {
    throw new Error('Chip must have a label.')
  }

  return { icon, avatar, label }
}

Chip.renderable = true

export { Chip }
